import { Button, DatePicker, Table, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SalesRepFilter from '../../components/ui/SalesRepFilter';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';
import { getLeadCheckinsReport } from '../../redux/actions/reports';
import { formatCount, getDateRangeFormat } from '../../utils';
import { EyeFilled } from '@ant-design/icons';
import { ROLES } from '../../routes/Routes';
import CheckinsDetail from './Details';

const { RangePicker } = DatePicker

const startOfMonth = dayjs().startOf('month');
const endOfMonth = dayjs().endOf('month');

const defaultFilters = {
    start_date: startOfMonth,
    end_date: endOfMonth,
    user_id: null,
};

const CheckinsReport = () => {

    const { filters, applyFilters } = usePersistentFilters(defaultFilters)
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const { user } = useSelector((state) => state.authSlice);
    const isDownload = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);

    const fetchCheckinsReport = async (body) => {
        try {
            setLoading(true);
            const res = await getLeadCheckinsReport(body);
            if (res?.ack) {
                setReports(res.leads || []);
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Sales Rep',
            dataIndex: 'salesrep_name',
            key: 'salesrep_name',
        },
        {
            title: 'Total Checkins',
            dataIndex: 'total_checkins',
            key: 'total_checkins',
            render: text => formatCount(text)
        },
        {
            title: 'Average Time',
            dataIndex: 'avg_time',
            key: 'avg_time',
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <Button
                    size="small"
                    color="primary"
                    variant="filled"
                    icon={<EyeFilled />}
                    onClick={() => {
                        setDetailOpen(true);
                        setDetailRecord(record);
                    }}
                />
            ),
        },
    ];

    useEffect(() => {
        const { start_date, end_date } = getDateRangeFormat([
            filters.start_date,
            filters.end_date,
        ]);
        fetchCheckinsReport(
            applyFilters({
                start_date, end_date
            })
        );
    }, []);


    return (
        <div className="promotions-page">
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className="page-title">
                        Checkins Report
                    </Typography.Title>
                </div>
                <div className="flex align-items-center gap-2 flex-wrap">
                    <RangePicker
                        className="w-full lg:max-w-20rem"
                        format="DD-MM-YYYY"
                        size="large"
                        value={[
                            filters.start_date && dayjs(filters.start_date),
                            filters.end_date && dayjs(filters.end_date),
                        ]}
                        onChange={(dates) => {
                            const { start_date, end_date } = getDateRangeFormat(dates);
                            const nextFilters = applyFilters({
                                start_date,
                                end_date,
                            });
                            fetchCheckinsReport({ ...nextFilters, page: 1 });
                        }}
                        allowClear={false}
                    />
                    <SalesRepFilter
                        value={filters.user_id}
                        onChange={(value) => {
                            const nextFilters = applyFilters({ user_id: value });
                            fetchCheckinsReport({ ...nextFilters, page: 1 });
                        }}
                    />
                </div>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id}
                rowClassName={(record) => record?.terms?.includes('Cash on Delivery') ? 'cod-row' : ''}
                loading={loading}
                dataSource={reports}
                pagination={false}
                scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                components={{
                    body: {
                        cell: ({ className, ...rest }) => (
                            <td
                                {...rest}
                                className={reports?.length && clsx(className, 'py-1')}
                            />
                        ),
                    },
                }}
            />

            <CheckinsDetail
                open={detailOpen}
                onClose={() => { setDetailOpen(false); setDetailRecord(null); }}
                filter={{
                    ...detailRecord,
                    ...getDateRangeFormat([filters.start_date, filters.end_date]),
                }}
                isDownload={isDownload}
            />
        </div>
    )
}

export default CheckinsReport